// Typed client for the SimpleNotation API (§6.1).
import type { Score } from '@simplenotation/core';

export interface ApiUser {
  id: string;
  email: string | null;
  name: string;
  guest: boolean;
}

export type Privacy = 'private' | 'unlisted' | 'public';

export interface ScoreMeta {
  slug: string;
  title: string;
  privacy: Privacy;
  latestVersion: number;
  owner: { id: string; name: string };
  canEdit: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ScoreSummary {
  slug: string;
  title: string;
  privacy: Privacy;
  latestVersion: number;
  createdAt: string;
  updatedAt: string;
}

export interface VersionInfo {
  version: number;
  createdAt: string;
  author: { id: string; name: string } | null;
  label: string | null;
}

export interface BugReport {
  message: string;
  expected?: string;
  email?: string;
  context: Record<string, string | number | boolean>;
  errors: string[];
  score?: Score;
  website?: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly body?: Record<string, unknown>,
  ) {
    super(message);
  }
}

async function req<T>(method: string, path: string, body?: unknown, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  let payload: BodyInit | undefined;
  if (body instanceof FormData) payload = body;
  else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(path, { method, headers, body: payload, credentials: 'same-origin', ...init });
  } catch {
    throw new ApiError(0, 'network', 'Can’t reach the server. Check your connection.');
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: Record<string, unknown> = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    if (!res.ok) throw new ApiError(res.status, 'bad_response', `Server error (${res.status})`);
  }
  if (!res.ok) {
    throw new ApiError(res.status, String(data.error ?? 'error'), String(data.message ?? data.error ?? `Request failed (${res.status})`), data);
  }
  return data as T;
}

export const api = {
  health: () => req<{ ok: boolean; db: boolean }>('GET', '/api/health'),
  me: () => req<{ user: ApiUser | null }>('GET', '/api/me'),
  guest: () => req<{ user: ApiUser }>('POST', '/api/auth/guest'),
  signup: (email: string, password: string, name?: string, captcha?: string, website?: string) =>
    req<{ user: ApiUser }>('POST', '/api/auth/signup', { email, password, name, captcha, website }),
  login: (email: string, password: string) => req<{ user: ApiUser }>('POST', '/api/auth/login', { email, password }),
  logout: () => req<void>('POST', '/api/auth/logout'),
  providers: () => req<{ google: boolean; turnstileSiteKey: string | null; passwordReset: boolean; bugReport: boolean }>('GET', '/api/auth/providers'),
  reportBug: (report: BugReport) => req<{ ok: true }>('POST', '/api/bug-report', report),
  forgotPassword: (email: string, captcha?: string) => req<{ ok: true }>('POST', '/api/auth/forgot', { email, captcha }),
  getPrefs: () => req<{ prefs: Record<string, unknown> }>('GET', '/api/me/editor-prefs'),
  putPrefs: (prefs: Record<string, unknown>) => req<{ prefs: Record<string, unknown> }>('PUT', '/api/me/editor-prefs', { prefs }),
  listScores: () => req<{ scores: ScoreSummary[] }>('GET', '/api/scores'),
  createScore: (title: string, notation?: Score) => req<{ meta: ScoreMeta }>('POST', '/api/scores', { title, notation }),
  getMeta: (slug: string) => req<{ meta: ScoreMeta }>('GET', `/api/scores/${encodeURIComponent(slug)}`),
  patchMeta: (slug: string, patch: { title?: string; privacy?: Privacy }) => req<{ meta: ScoreMeta }>('PATCH', `/api/scores/${encodeURIComponent(slug)}`, patch),
  deleteScore: (slug: string) => req<void>('DELETE', `/api/scores/${encodeURIComponent(slug)}`),
  getNotation: (slug: string, v?: number) => req<{ version: number; notation: Score }>('GET', `/api/scores/${encodeURIComponent(slug)}/notation${v ? `?v=${v}` : ''}`),
  putNotation: (slug: string, baseVersion: number, notation: Score, label?: string) =>
    req<{ version: number; meta: ScoreMeta }>('PUT', `/api/scores/${encodeURIComponent(slug)}/notation`, { baseVersion, notation, label }),
  versions: (slug: string) => req<{ versions: VersionInfo[] }>('GET', `/api/scores/${encodeURIComponent(slug)}/versions`),
  restore: (slug: string, v: number) => req<{ version: number; meta: ScoreMeta }>('POST', `/api/scores/${encodeURIComponent(slug)}/versions/${v}/restore`),
  fork: (slug: string) => req<{ meta: ScoreMeta }>('POST', `/api/scores/${encodeURIComponent(slug)}/fork`),
  importFile: (file: File) => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return req<{ notation: Score; format: string }>('POST', '/api/import', fd);
  },
};
